import { randomInt } from "../shared/rng";
import {
  BUTTON,
  FIGHTER,
  PHASE,
  type AbilityDef,
  type FighterState,
  type InputBits,
  type MatchRules,
  type MatchState,
} from "../sim";
import { FAR, MID, weightOf, type Situation } from "./choice";
import { planInput, planOf, type Plan } from "./plan";
import { routesFor, type Route } from "./routes";

/**
 * Бот для автоигр (GAME-29) и против человека — в полную силу (Джон 04.11.2026: «я не хочу чтобы бот был спокойней
 * против человека»). Выдаёт те же биты, что геймпад, поэтому проверяет весь путь мира. Приёмы — из тех же данных, что
 * у игрока (bot/routes.ts), выбор — по положению и новизне (bot/choice.ts). Свой ГСЧ — вне снимка мира, но
 * детерминированный: повтор боя тот же.
 */
export interface Bot {
  rng: number;
  readonly rules: MatchRules;
  /** Путь к приёму, который бот сейчас ведёт. */
  plan: Plan | null;
  /** Простые нажатия без приёма: шаг, прыжок, блок. */
  queue: InputBits[];
  /** Сколько раз у бота в этом бою начинался каждый приём — новизна выбора; и какой шёл прошлым кадром. */
  readonly uses: Map<string, number>;
  lastAbility: number;
  lastAbilityFrame: number;
  /** Состояние прошлым кадром и сколько ещё кадров «только что отбил в блоке». */
  lastState: number;
  afterBlock: number;
}

export const createBot = (seed: number, rules: MatchRules): Bot => ({
  rng: seed | 0 || 1,
  rules,
  plan: null,
  queue: [],
  uses: new Map(),
  lastAbility: -1,
  lastAbilityFrame: 0,
  lastState: FIGHTER.idle,
  afterBlock: 0,
});

/** Края арены (пиксели мира): у стены бот не стоит под серией, а уходит. */
const ARENA_LEFT = 64;
const ARENA_RIGHT = 896;
const CORNER = 80;
/** Ответ после блока — столько кадров после отдачи. */
const AFTER_BLOCK = 24;
/** Полная шкала Ярости — суперприём. */
const SUPER_METER = 3000;

const routeCache = new WeakMap<readonly AbilityDef[], ReadonlyMap<string, Route>>();
function routesOf(kit: readonly AbilityDef[]): ReadonlyMap<string, Route> {
  let routes = routeCache.get(kit);
  if (!routes) {
    routes = routesFor(kit);
    routeCache.set(kit, routes);
  }
  return routes;
}

function roll(bot: Bot, range: number): number {
  const next = randomInt(bot.rng, range);
  bot.rng = next.state;
  return next.value;
}

const toward = (me: FighterState, other: FighterState): number => (other.x > me.x ? BUTTON.right : BUTTON.left);
const awayFrom = (me: FighterState, other: FighterState): number => (other.x > me.x ? BUTTON.left : BUTTON.right);
const fill = (count: number, bits: InputBits): InputBits[] => new Array<InputBits>(count).fill(bits);

/**
 * Защита в полную силу (консультант 05.11.2026: «бот не защищается… загнан в угол одним приёмом»): соперник бьёт рядом —
 * чаще всего блок (снизу, если тот присел); зажат у стены без Ярости на уход — прыжок через соперника.
 */
function defend(bot: Bot, me: FighterState, other: FighterState, situation: Situation): InputBits[] | null {
  const free = me.state === FIGHTER.idle || me.state === FIGHTER.walkForward || me.state === FIGHTER.walkBack;
  if (!free && me.state !== FIGHTER.block && me.state !== FIGHTER.crouchBlock) return null;
  const chance = roll(bot, 100);
  if (other.state === FIGHTER.ability && situation.distance < MID && chance < 75) {
    const low = (other.lastBits & BUTTON.down) !== 0;
    return fill(10, low ? BUTTON.block | BUTTON.down : BUTTON.block);
  }
  if (situation.cornered && situation.meter < 1000 && chance < 30)
    return [BUTTON.up | toward(me, other), ...fill(16, 0)];
  return null;
}

/** Сбит и лежит: атака при подъёме, кувырок к сопернику, подъём прыжком или просто встать. */
function fromFloor(bot: Bot, me: FighterState, other: FighterState, routes: ReadonlyMap<string, Route>): void {
  const rise = [...routes.values()].find((route) => route.needs.lying);
  const choice = roll(bot, 4);
  if (choice === 0 && rise) {
    choose(bot, rise);
    return;
  }
  bot.queue = choice === 1 ? fill(4, toward(me, other)) : choice === 2 ? fill(4, BUTTON.up) : fill(4, 0);
}

function choose(bot: Bot, route: Route, distance = 0): void {
  const near = route.needs.near;
  bot.plan = planOf(route, near !== undefined && distance > near ? near : undefined);
}

/** Нападение: путь к приёму по весам или простое движение — подойти, отойти, прыжок, выждать. */
function attack(
  bot: Bot,
  me: FighterState,
  other: FighterState,
  kit: readonly AbilityDef[],
  situation: Situation,
): void {
  const routes = routesOf(kit);
  const options: { weight: number; route?: Route; moves?: InputBits[] }[] = [];
  for (const route of routes.values()) {
    const ability = kit.find((candidate) => candidate.id === route.id);
    if (!ability) continue;
    const weight = weightOf(route, ability, situation, bot.uses.get(route.id) ?? 0);
    if (weight > 0) options.push({ weight, route });
  }
  const { distance } = situation;
  options.push(
    { weight: distance > FAR ? 14 : distance > MID ? 6 : 0.5, moves: fill(12, toward(me, other)) },
    { weight: situation.cornered ? 0 : 0.8, moves: fill(10, awayFrom(me, other)) },
    { weight: 1, moves: [BUTTON.up | toward(me, other), ...fill(14, 0)] },
    { weight: 1.5, moves: fill(8, 0) },
  );
  const total = options.reduce((sum, option) => sum + option.weight, 0);
  let pick = (roll(bot, 1_000_000) / 1_000_000) * total;
  for (const option of options) {
    pick -= option.weight;
    if (pick > 0) continue;
    if (option.route) choose(bot, option.route, distance);
    else bot.queue = [...(option.moves ?? [])];
    return;
  }
}

function situationOf(bot: Bot, me: FighterState, other: FighterState): Situation {
  const distance = Math.abs(other.x - me.x) / 256;
  const x = me.x / 256;
  return {
    distance,
    crouching: other.state === FIGHTER.crouch || other.state === FIGHTER.crouchBlock,
    afterBlock: bot.afterBlock > 0,
    cornered: (x < ARENA_LEFT + CORNER || x > ARENA_RIGHT - CORNER) && distance < 120,
    meter: me.meter,
    lying: me.state === FIGHTER.knockdown,
    opponentDown: other.state === FIGHTER.knockdown || other.state === FIGHTER.thrown,
  };
}

/** Приём начался (новый или тот же заново) — в счёт новизны: считается то, что вышло, а не то, что бот задумал. */
function countStart(bot: Bot, me: FighterState, kit: readonly AbilityDef[]): void {
  const inAbility = me.state === FIGHTER.ability;
  const started = inAbility && (me.ability !== bot.lastAbility || me.abilityFrame < bot.lastAbilityFrame);
  const id = started ? kit[me.ability]?.id : undefined;
  if (id) bot.uses.set(id, (bot.uses.get(id) ?? 0) + 1);
  bot.lastAbility = inAbility ? me.ability : -1;
  bot.lastAbilityFrame = me.abilityFrame;
}

export function botInput(bot: Bot, state: MatchState, side: 0 | 1): InputBits {
  const me = state.fighters[side];
  const other = state.fighters[side === 0 ? 1 : 0];
  if (state.phase !== PHASE.fight || state.cinematic >= 0) {
    bot.plan = null;
    bot.queue = [];
    return 0;
  }
  const kit = bot.rules.kits[side];
  if (bot.lastState === FIGHTER.blockstun && me.state !== FIGHTER.blockstun) bot.afterBlock = AFTER_BLOCK;
  else if (bot.afterBlock > 0) bot.afterBlock--;
  bot.lastState = me.state;
  countStart(bot, me, kit);
  const planned = follow(bot, me, other, kit, state.frame + 1);
  if (planned !== null) return planned;
  const queued = bot.queue.shift();
  if (queued !== undefined) return queued;
  const situation = situationOf(bot, me, other);
  if (me.state === FIGHTER.knockdown) {
    fromFloor(bot, me, other, routesOf(kit));
  } else {
    const reflex = defend(bot, me, other, situation);
    if (reflex) bot.queue = reflex;
    else if (me.meter >= SUPER_METER && situation.distance < 140 && me.state <= FIGHTER.crouch) {
      bot.queue = [0, 0];
      return BUTTON.super | BUTTON.enhance;
    } else if (me.state !== FIGHTER.ability && me.state !== FIGHTER.hitstun && me.state !== FIGHTER.blockstun)
      attack(bot, me, other, kit, situation);
  }
  return follow(bot, me, other, kit, state.frame + 1) ?? bot.queue.shift() ?? 0;
}

/** Следующий кадр пути, который ведёт бот; null — пути нет, пройден или брошен. */
function follow(
  bot: Bot,
  me: FighterState,
  other: FighterState,
  kit: readonly AbilityDef[],
  frame: number,
): InputBits | null {
  if (!bot.plan) return null;
  const bits = planInput(bot.plan, me, other, kit, frame);
  if (bits === null) bot.plan = null;
  return bits;
}
